import { visitorsUniqueKeyEqual } from "./visitorId";

function conversationMessageCount(conversation) {
  if (!conversation) return 0;
  if (Array.isArray(conversation?.messages)) return conversation.messages.length;
  return Number(conversation?.message_count) || 0;
}

function pickBetterConversation(a, b) {
  if (!a) return b ?? null;
  if (!b) return a ?? null;

  const aCount = conversationMessageCount(a);
  const bCount = conversationMessageCount(b);
  if (bCount > aCount) return b;
  if (aCount > bCount) return a;

  const aTime = new Date(a?.updatedAt ?? a?.createdAt ?? 0).getTime();
  const bTime = new Date(b?.updatedAt ?? b?.createdAt ?? 0).getTime();
  return bTime >= aTime ? b : a;
}

function conversationKey(conversation) {
  return String(conversation?._id ?? conversation?.id ?? "");
}

function mergePastConversations(...sources) {
  const byId = new Map();

  for (const source of sources) {
    for (const conversation of source?.past_conversations ?? []) {
      const key = conversationKey(conversation);
      if (!key) continue;
      byId.set(key, pickBetterConversation(byId.get(key), conversation));
    }
  }

  return Array.from(byId.values()).sort(
    (a, b) =>
      new Date(a?.createdAt ?? 0).getTime() - new Date(b?.createdAt ?? 0).getTime()
  );
}

function mergeCurrentConversation(...sources) {
  return sources.reduce(
    (best, source) => pickBetterConversation(best, source?.current_conversation),
    null
  );
}

export function getTranscriptConversations(visitor) {
  if (!visitor) return [];

  const past = visitor?.past_conversations ?? [];
  const current = visitor?.current_conversation;
  const conversations = current ? [...past, current] : [...past];

  return conversations.filter(Boolean);
}

export function getTranscriptMessages(visitor) {
  const seen = new Set();
  const messages = [];

  for (const conversation of getTranscriptConversations(visitor)) {
    for (const message of conversation?.messages ?? []) {
      const key =
        message?.uniqueMessageID ??
        message?._id ??
        `${message?.time ?? message?.createdAt ?? ""}-${message?.senderID ?? ""}-${message?.message ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      messages.push(message);
    }
  }

  return messages.sort(
    (a, b) =>
      new Date(a?.time ?? a?.createdAt ?? 0).getTime() -
      new Date(b?.time ?? b?.createdAt ?? 0).getTime()
  );
}

export function computePastVisits(visitor) {
  const visits = visitor?.visits;
  if (visits == null || Number.isNaN(Number(visits))) {
    return 0;
  }
  return Math.max(0, Number(visits) - 1);
}

export function computePastChats(visitor) {
  return visitor?.past_conversations?.length ?? 0;
}

/**
 * Merge visitor profile fragments (table row, API fetch, live socket).
 * Prefers the highest visit count and the longest past_conversations list.
 */
export function mergeVisitorProfile(...sources) {
  const defined = sources.filter(Boolean);
  if (!defined.length) return null;

  const merged = Object.assign({}, ...defined);

  const visits = defined.reduce((max, source) => {
    const value = Number(source?.visits);
    if (!Number.isNaN(value) && value > max) return value;
    return max;
  }, 0);

  if (visits > 0) {
    merged.visits = visits;
  }

  merged.past_conversations = mergePastConversations(...defined);
  merged.current_conversation = mergeCurrentConversation(...defined);

  return merged;
}

export function visitorProfileMatches(profile, uniqueId) {
  if (!profile || uniqueId == null) return false;
  return visitorsUniqueKeyEqual(profile?.unique_id, uniqueId);
}
